import type { PlaceAddress } from '../api/GoogleLocationAPI.model'

// "31-019 Kraków", or a five- or four-digit code as in Germany or Austria.
const POSTCODE_AND_CITY = /^(\d{2}-\d{3}|\d{4,5})\s+(.+)$/
// "Floriańska 1", "Marszałkowska 10/16", "aleja Jana Pawła II 43A".
const STREET_AND_NUMBER = /^(.*\S)\s+(\d+\p{L}?(?:\/\d+\p{L}?)?)$/u

/**
 * Google's one-line address in the parts the venue's address has, for a
 * core-api that answers the pick without them.
 *
 * Read from the shape Google writes in Poland: "Floriańska 1, 31-019 Kraków,
 * Polska". The street line comes first and the postcode leads the city's. A
 * part that cannot be told apart stays empty, so the owner's stays.
 */
export function addressFromFormatted(formatted: string): PlaceAddress {
  const parts = formatted
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)

  /*
    A bare four or five digits leading the first part is a house number
    ("1600 Amphitheatre Pkwy, Mountain View"), not a postcode: a code of that
    shape always follows the street line. Only Poland's dashed one may come
    first, for a place with no street.
  */
  const cityAt = parts.findIndex((part, index) => {
    const match = part.match(POSTCODE_AND_CITY)
    return !!match && (index > 0 || match[1].includes('-'))
  })
  if (cityAt === -1) {
    return { street: '', buildingNumber: '', postalCode: '', city: '' }
  }

  const [, postalCode, city] = parts[cityAt].match(POSTCODE_AND_CITY) ?? []
  const streetLine = cityAt > 0 ? parts[0] : ''
  const [, street, buildingNumber] = streetLine.match(STREET_AND_NUMBER) ?? [
    '',
    streetLine,
    ''
  ]

  return { street, buildingNumber, postalCode, city }
}
